import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { Flex, QueryLoader, Text, toast } from "@jsq/ds/core";
import {
  useGetOwnersQuery,
  useSubscriptionSubmitIdentificationKycMutation,
} from "../../graphql/supergraph.index";
import { BaseDocumentType } from "../../graphql/supergraphOperations";
import useMultiDocumentProcessor from "../../hooks/documents/useMultiDocumentProcessor";
import { DefaultModal } from "../common/DefaultModal";
import { textColorNeutral800 } from "../common/style_vanilla.css";
import { DocumentData } from "../documents/DocumentCollector/types";
import { MultiDocumentCollector } from "../documents/MultiDocumentCollector";
import { titleStyle } from "../IndividualLandingScreen/style_vanilla.css";
import { provideLegalName } from "../ProfileTree/utils";
import { JointLandingScreenProps } from "./types";
import { getExistingProfileDocumentsOrDefault } from "./utils";

export const JointLandingScreen: React.FC<JointLandingScreenProps> = ({
  caseId,
  onStepCompletion,
  subscriptionId,
  arenaId,
  subscriptionInReview = false,
}) => {
  const history = useHistory();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showBackWarningModal, setShowBackWarningModal] = useState(false);

  const ownerResults = useGetOwnersQuery({
    variables: {
      caseId: Number(caseId),
    },
    fetchPolicy: "no-cache",
    skip: !caseId,
  });
  const owners =
    ownerResults.data?.complianceCase?.investingProfile?.beneficialOwners.map(
      (owner) => owner.childProfile,
    ) || [];
  const documents = ownerResults.data?.complianceCase?.documents ?? [];
  const documentTypes =
    (
      ownerResults.data?.complianceCase?.investingProfile?.rulesEngine as {
        documentTypes: { baseType: BaseDocumentType }[];
      }
    )?.documentTypes
      .map((dt) => dt.baseType)
      .sort((a, b) => a.localeCompare(b)) ?? [];

  const documentCollections = owners.map((owner) => ({
    profileId: owner.id,
    title: provideLegalName(owner),
    documentDetails: getExistingProfileDocumentsOrDefault(documentTypes, owner.id, documents),
  }));
  const { processDocuments, loading } = useMultiDocumentProcessor(caseId);
  const [subscriptionSubmitIdentificationKycMutation] =
    useSubscriptionSubmitIdentificationKycMutation();

  /**
   * Handle unsaved changes updates from MultiDocumentCollector
   */
  const handleUnsavedChangesChange = useCallback((hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  }, []);

  /**
   * Handle navigation back with unsaved changes check
   */
  const handleNavigateBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowBackWarningModal(true);
    } else {
      history.goBack();
    }
  }, [hasUnsavedChanges, history]);

  const handleDocumentProcessing = useCallback(
    async (newFiles: DocumentData[], deleteDocIds: number[]) => {
      try {
        await processDocuments(newFiles, deleteDocIds);
        toast.success("Documents updated successfully");

        if (!subscriptionInReview) {
          // Then submit identity verification
          await subscriptionSubmitIdentificationKycMutation({
            variables: {
              subscriptionId,
              arenaId,
            },
          });
        }

        setHasUnsavedChanges(false); // Clear unsaved changes after successful submission
        onStepCompletion();
      } catch (error) {
        toast.error("Failed to update documents");
      }
    },
    [
      processDocuments,
      subscriptionSubmitIdentificationKycMutation,
      subscriptionId,
      arenaId,
      onStepCompletion,
    ],
  );

  return (
    <QueryLoader result={[ownerResults]}>
      <Flex flexDirection="column" gap="20px">
        <Text variant="h3" className={titleStyle}>
          Identification
        </Text>
        <Text
          variant="regular-md"
          className={textColorNeutral800}
        >{`Due to regulatory requirements, the following documents are required. You can skip this step for now if you don't have these documents available. You will need to return and upload any required documents before signing this subscription.`}</Text>
        <MultiDocumentCollector
          caseId={caseId}
          documentCollections={documentCollections}
          onComplete={(newUploadFiles, deletedIds) => {
            void handleDocumentProcessing(newUploadFiles, deletedIds);
          }}
          onNavigateBack={handleNavigateBack}
          onUnsavedChangesChange={handleUnsavedChangesChange}
          isSubmitting={loading}
          missingDocumentsMessage="You can continue and sign the subscription, but the subscription cannot be accepted until all required docs are uploaded."
        />
      </Flex>
      <DefaultModal
        show={showBackWarningModal}
        title="Warning"
        body="You have unsaved changes. Are you sure you want to go back without submitting?"
        negBtnText="Cancel"
        posBtnText="Back"
        posBtnVariant="danger"
        onClose={() => setShowBackWarningModal(false)}
        onConfirm={() => {
          setShowBackWarningModal(false);
          setHasUnsavedChanges(false);
          history.goBack();
        }}
      />
    </QueryLoader>
  );
};
